import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, Signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { WebsiteConfigFormExpansionPanelComponent } from '../website-config-form-expansion-panel/website-config-form-expansion-panel.component';
import { WebsiteStateService } from '../../../../../state-management/services/website-state.service';
import { BusinessConfiguration, LegalData } from '../../../../../state-management/model/interfaces/state.model';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BusinessType } from '../../../../../state-management/model/enums/state.enums';
import { debounceTime, distinctUntilChanged, filter, startWith } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AdminUserInputComponent } from '../../../admin-user-input/admin-user-input.component';
import { Subscription } from 'rxjs';

interface ImprintForm {
  imprint: {
    useImprintTemplate: boolean;
    managerContentName: {
      value: string;
      syncWithContactData: boolean;
    };
    managerContentStreet: {
      value: string;
      syncWithContactData: boolean;
    };
    managerContentCity: {
      value: string;
      syncWithContactData: boolean;
    };
    managerContentPhone: {
      value: string;
      syncWithContactData: boolean;
    };
    managerContentMail: {
      value: string;
      syncWithContactData: boolean;
    };

  };
  dataPrivacy: {
    useDataPrivacyTemplate: boolean;
    managerDataPrivacyName: {
      value: string;
      syncWithContactData: boolean;
    };
    managerDataPrivacyStreet: {
      value: string;
      syncWithContactData: boolean;
    };
    managerDataPrivacyCity: {
      value: string;
      syncWithContactData: boolean;
    };
    managerDataPrivacyPhone: {
      value: string;
      syncWithContactData: boolean;
    };
    managerDataPrivacyMail: {
      value: string;
      syncWithContactData: boolean;
    };
  }
}

@Component({
    selector: 'app-website-config-form-legal-notice',
    standalone: true,
    imports: [
      CommonModule,
      MatFormFieldModule,
      FormsModule,
      MatInputModule,
      MatCheckbox,
      MatTooltipModule,
      MatIconModule,
      ReactiveFormsModule,
      MatButtonModule,
      MatSelectModule,
      MatSlideToggleModule,
      WebsiteConfigFormExpansionPanelComponent,
      AdminUserInputComponent,
    ],
    templateUrl: './website-config-form-legal-notice.component.html',
    styleUrl: './website-config-form-legal-notice.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebsiteConfigFormLegalNoticeComponent implements OnInit, OnDestroy {

  public metaData: Signal<BusinessConfiguration> = this.websiteState.configuration;
  public form!: FormGroup;
  public showImpressumFields: boolean = false;
  public showDataPrivacyFields: boolean = false;
  public businessContactLabel: string | undefined = undefined; 
  public businessTypes = Object.values(BusinessType);
  
  private legalData: LegalData = this.metaData().legalData;
  private isInitialLoad = 0;
  private formSubscription: Subscription = new Subscription();

  constructor( private websiteState: WebsiteStateService, private fb: FormBuilder ) { 
    console.log("Constructor called");
    effect(() => {
      console.log("IN EFFECT LEGAL", this.metaData());
      this.#updateForm(this.metaData().legalData);
    });
  }

  public fieldsContentManager = [
    { key: 'managerContentName', label: 'Name', placeholder: 'Name Verantwortlicher Inhalt' },
    { key: 'managerContentStreet', label: 'Straße + Haus-Nr.', placeholder: 'Straße + Haus-Nr.' },
    { key: 'managerContentCity', label: 'PLZ und Stadt', placeholder: 'PLZ und Stadt' },
    { key: 'managerContentPhone', label: 'Telefonnummer', placeholder: 'Telefonnummer' },
    { key: 'managerContentMail', label: 'E-Mail', placeholder: 'E-Mail', errorKey: 'email', errorMessage: 'E-Mail ungültig.' },
  ];

  public fieldsDataPrivacyManager = [
    { key: 'managerDataPrivacyName', label: 'Name', placeholder: 'Name' },
    { key: 'managerDataPrivacyStreet', label: 'Straße + Haus-Nr.', placeholder: 'Straße + Haus-Nr.' },
    { key: 'managerDataPrivacyCity', label: 'PLZ und Stadt', placeholder: 'PLZ und Stadt' },
    { key: 'managerDataPrivacyPhone', label: 'Telefonnummer', placeholder: 'Telefonnummer' },
    { key: 'managerDataPrivacyMail', label: 'E-Mail', placeholder: 'E-Mail', errorKey: 'email', errorMessage: 'E-Mail ungültig.' },
  ];
  
  

  ngOnInit(): void {
    console.log("IN INIT LEGAL");
    this.form = this.fb.group({
      imprint: this.fb.group({
        useImprintTemplate: [this.legalData.useImprintTemplate],
        customImprintContent: [this.legalData.customImprintContent],
        managerContentName: this.createSyncableControl(this.legalData.managerContentName.value, this.legalData.managerContentName.syncWithContactData),
        managerContentStreet: this.createSyncableControl(this.legalData.managerContentStreet.value, this.legalData.managerContentStreet.syncWithContactData),
        managerContentCity: this.createSyncableControl(this.legalData.managerContentCity.value, this.legalData.managerContentCity.syncWithContactData),
        managerContentPhone: this.createSyncableControl(this.legalData.managerContentPhone.value, this.legalData.managerContentPhone.syncWithContactData),
        managerContentMail: this.createSyncableControl(this.legalData.managerContentEmail.value, this.legalData.managerDataPrivacyEmail.syncWithContactData, [Validators.email]),
        
      }),
      dataPrivacy: this.fb.group({
        useDataPrivacyTemplate: [this.legalData.useDataPrivacyTemplate],
        customDataPrivacyContent: [this.legalData.customDataPrivacyContent],
        managerDataPrivacyName: this.createSyncableControl(this.legalData.managerDataPrivacyName.value, this.legalData.managerDataPrivacyName.syncWithContactData),
        managerDataPrivacyStreet: this.createSyncableControl(this.legalData.managerDataPrivacyStreet.value, this.legalData.managerDataPrivacyStreet.syncWithContactData),
        managerDataPrivacyCity: this.createSyncableControl(this.legalData.managerDataPrivacyCity.value, this.legalData.managerDataPrivacyCity.syncWithContactData),
        managerDataPrivacyPhone: this.createSyncableControl(this.legalData.managerDataPrivacyPhone.value, this.legalData.managerDataPrivacyPhone.syncWithContactData),
        managerDataPrivacyMail: this.createSyncableControl(this.legalData.managerDataPrivacyEmail.value, this.legalData.managerDataPrivacyEmail.syncWithContactData, [Validators.email]) 
      })
    });

      // TODO: SVI - This could produces a update in the state, which is not necessary, but shown as indicator in the UI 
      this.formSubscription = this.form.valueChanges.pipe(
        debounceTime(500), 
        distinctUntilChanged(), 
        startWith(this.form.value),
        filter(() => this.form.valid)
      ).subscribe(async () => {
        console.log("IN SUBSCRIBE LEGAL");
        if(this.isInitialLoad > 1) await this.#saveFormChanges();
        this.isInitialLoad += 1;
        console.log("isInitialLoad +++++", this.isInitialLoad);
        }
      );
      
  }

  
  ngOnDestroy(): void {
    if(this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  private createSyncableControl(initValue: any, syncValue: boolean, validators: any[] = []): FormGroup {
    return this.fb.group({
      value: [{ value: initValue, disabled: syncValue }, ...validators],
      syncWithContactData: [syncValue]
    });
  }

  public toggleInput(formGroupName: string): void {
    this.form.get(`${formGroupName}.value`)?.[this.form.get(`${formGroupName}.syncWithContactData`)?.value ? 'disable' : 'enable']({emitEvent:false});
  }

  private updateValueIfSyncWithContactData(controlName: string, correspondingValue: any): void {
    const syncControl = this.form.get(`${controlName}.syncWithContactData`);
    const valueControl = this.form.get(`${controlName}`)?.get('value');
    if (syncControl?.value && valueControl) {
      if (valueControl.value != correspondingValue) {
        valueControl.setValue(correspondingValue);
      }
    }
  }

  #updateForm(legalData: LegalData): void {
    console.log("IN UPDATE LEGAL");
    this.updateValueIfSyncWithContactData('imprint.managerContentName', legalData.businessName);
    this.updateValueIfSyncWithContactData('imprint.managerContentStreet', legalData.businessStreet.value);
    this.updateValueIfSyncWithContactData('imprint.managerContentCity', legalData.businessCity.value);
    this.updateValueIfSyncWithContactData('imprint.managerContentPhone', legalData.businessContactPhone.value);
    this.updateValueIfSyncWithContactData('imprint.managerContentEmail', legalData.businessContactEmail.value);
    this.updateValueIfSyncWithContactData('dataPrivacy.managerDataPrivacyName', legalData.businessName);
    this.updateValueIfSyncWithContactData('dataPrivacy.managerDataPrivacyStreet', legalData.businessStreet.value);
    this.updateValueIfSyncWithContactData('dataPrivacy.managerDataPrivacyCity', legalData.businessCity.value);
    this.updateValueIfSyncWithContactData('dataPrivacy.managerDataPrivacyPhone', legalData.businessContactPhone.value);
    this.updateValueIfSyncWithContactData('dataPrivacy.managerDataPrivacyEmail', legalData.businessContactEmail.value);
  }

  async #saveFormChanges(): Promise<void> {
    const imprintFormData: ImprintForm = this.form.getRawValue();
    this.websiteState.updateConfiguration({
      legalData: {
          ...this.metaData().legalData,
          useImprintTemplate: imprintFormData.imprint.useImprintTemplate,
          managerContentName: { value: imprintFormData.imprint.managerContentName.value, syncWithContactData: imprintFormData.imprint.managerContentName.syncWithContactData },
          managerContentStreet: { value: imprintFormData.imprint.managerContentStreet.value, syncWithContactData: imprintFormData.imprint.managerContentStreet.syncWithContactData },
          managerContentCity: { value: imprintFormData.imprint.managerContentCity.value, syncWithContactData: imprintFormData.imprint.managerContentCity.syncWithContactData },
          managerContentEmail: { value: imprintFormData.imprint.managerContentMail.value, syncWithContactData: imprintFormData.imprint.managerContentMail.syncWithContactData },
          managerContentPhone: { value: imprintFormData.imprint.managerContentPhone.value, syncWithContactData: imprintFormData.imprint.managerContentPhone.syncWithContactData },
          useDataPrivacyTemplate: imprintFormData.dataPrivacy.useDataPrivacyTemplate,
          managerDataPrivacyName: { value: imprintFormData.dataPrivacy.managerDataPrivacyName.value, syncWithContactData: imprintFormData.dataPrivacy.managerDataPrivacyName.syncWithContactData },
          managerDataPrivacyStreet: { value: imprintFormData.dataPrivacy.managerDataPrivacyStreet.value, syncWithContactData: imprintFormData.dataPrivacy.managerDataPrivacyStreet.syncWithContactData },
          managerDataPrivacyCity: { value: imprintFormData.dataPrivacy.managerDataPrivacyCity.value, syncWithContactData: imprintFormData.dataPrivacy.managerDataPrivacyCity.syncWithContactData },
          managerDataPrivacyEmail: { value: imprintFormData.dataPrivacy.managerDataPrivacyMail.value, syncWithContactData: imprintFormData.dataPrivacy.managerDataPrivacyMail.syncWithContactData },
          managerDataPrivacyPhone: { value: imprintFormData.dataPrivacy.managerDataPrivacyPhone.value, syncWithContactData: imprintFormData.dataPrivacy.managerDataPrivacyPhone.syncWithContactData },
        },
    });
  }

  public saveCustomImprintContent(sanitizedHtml: string): void {
    this.websiteState.updateConfiguration({
      legalData: {
        ...this.metaData().legalData,
        customImprintContent: sanitizedHtml
      }
    });
  }

  public saveCustomDataPrivacyContent(sanitizedHtml: string): void {
    this.websiteState.updateConfiguration({
      legalData: {
        ...this.metaData().legalData,
        customDataPrivacyContent: sanitizedHtml
      }
    });
  }

}
